/* Pinned feedback — the PURE locator + capture geometry (W3-I).
 *
 * The product feedback feature (ui/feedbackPin.ts) captures exactly one pin on
 * a clicked element. This module computes the parts that are pure functions of
 * the DOM node + the click point, so they can be unit-tested under jsdom
 * without the toggle/POST machinery:
 *   - a STABLE CSS selector that re-finds the element across renders (prefers
 *     an id, then data-attributes the shell already stamps, then a bounded
 *     structural path), and
 *   - the click GEOMETRY: viewport coordinates, the element's rect, and the
 *     click position as a fraction WITHIN the element (relX/relY) so the pin is
 *     re-locatable even when layout shifts.
 *
 * These feed the pin record POSTed to /feedback (api/feedback.ts's FeedbackPin).
 */

/** Attributes the CRM shell stamps that make a good, stable hook for a selector
 * (checked in order). These are structural, not content-derived, so they
 * survive re-renders. */
const STABLE_ATTRS = ['data-area', 'data-route', 'data-view', 'data-sub', 'data-tab'] as const;

/** Max ancestors to walk before falling back to the tag path — keeps the
 * selector short and readable. */
const MAX_DEPTH = 6;

function isElement(n: Node | null): n is Element {
  return !!n && n.nodeType === 1;
}

/** A CSS-safe id/attr value check — an id with spaces or CSS metacharacters
 * can't go in a bare `#id`, so those fall through to the structural path. */
function cssSafe(v: string): boolean {
  return /^[A-Za-z_][\w-]*$/.test(v);
}

/** One selector segment for an element: `#id` when it has a safe unique id,
 * else `tag[attr="v"]` for the first stable attribute it carries, else
 * `tag:nth-of-type(k)` positioned among its same-tag siblings. */
function segment(el: Element): { sel: string; unique: boolean } {
  const tag = el.tagName.toLowerCase();
  const id = el.getAttribute('id');
  if (id && cssSafe(id)) return { sel: `#${id}`, unique: true };

  for (const attr of STABLE_ATTRS) {
    const v = el.getAttribute(attr);
    if (v && cssSafe(v)) return { sel: `${tag}[${attr}="${v}"]`, unique: false };
  }

  const parent = el.parentElement;
  if (!parent) return { sel: tag, unique: false };
  const sameTag = Array.from(parent.children).filter((c) => c.tagName === el.tagName);
  if (sameTag.length === 1) return { sel: tag, unique: false };
  const idx = sameTag.indexOf(el) + 1;
  return { sel: `${tag}:nth-of-type(${idx})`, unique: false };
}

export interface StableLocator {
  /** the computed CSS selector. */
  selector: string;
  /** true when the selector is anchored on a unique id (so it's exact); false
   * for a best-effort structural path (still recorded, honestly). */
  exact: boolean;
}

/** Build a stable selector for `el`, walking up to the nearest id-bearing
 * ancestor (or the document root). Stops early — and reports `exact: true` —
 * as soon as a segment is an id, since an id segment alone identifies the node.
 * Pure; deterministic for a given DOM. */
export function stableSelector(el: Element): StableLocator {
  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  let exact = false;
  while (isElement(node) && depth < MAX_DEPTH) {
    const seg = segment(node);
    parts.unshift(seg.sel);
    if (seg.unique) { exact = true; break; } // an id anchors the whole path
    node = node.parentElement;
    depth++;
  }
  return { selector: parts.join(' > '), exact };
}

/** A short, trimmed text label for the element — for human-readable pin
 * retrieval ("the button that said Send feedback"). Capped; whitespace
 * collapsed; empty for a node with no direct text. */
export function elementLabel(el: Element, cap = 80): string {
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  return text.length > cap ? `${text.slice(0, cap - 1)}…` : text;
}

export interface PinGeometry {
  /** click point in viewport (CSS) pixels, rounded. */
  x: number;
  y: number;
  /** the element's bounding rect at capture time. */
  rect: { x: number; y: number; w: number; h: number };
  /** the click position as a 0..1 fraction WITHIN the element (clamped) — the
   * layout-shift-resilient anchor. */
  relX: number;
  relY: number;
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/** Compute the capture geometry from a click point + the element's rect. Pure —
 * the rect is passed in (the UI reads it via getBoundingClientRect) so this is
 * testable without a real layout engine. */
export function pinGeometry(clientX: number, clientY: number, rect: DOMRectReadOnly | DOMRect): PinGeometry {
  const w = rect.width || 0;
  const h = rect.height || 0;
  return {
    x: Math.round(clientX),
    y: Math.round(clientY),
    rect: { x: Math.round(rect.left), y: Math.round(rect.top), w: Math.round(w), h: Math.round(h) },
    relX: Number(clamp01(w ? (clientX - rect.left) / w : 0).toFixed(4)),
    relY: Number(clamp01(h ? (clientY - rect.top) / h : 0).toFixed(4)),
  };
}
